@if (preview) {
  <header class="preview-header">
    <cf-text size="sm" color="white">
      {{ "booking.preview_header_text" | translate }}
      @if (templateID && editable) {
        <a
          cf-link
          size="sm"
          color="white"
          [underlined]="true"
          [href]="environment.appUrl + '/meeting/edit/' + templateID"
        >
          {{ "booking.preview_header_link" | translate }}
        </a>
      }
    </cf-text>

    <a cf-link size="sm" color="white" class="text-nowrap" (click)="clipboardButtonComponent.onCopy()">
      <cf-clipboard-button
        #clipboardButtonComponent
        [link]="bookingLink"
        iconClass="tw:text-white"
      ></cf-clipboard-button>
      {{ "actions.copy_link" | translate }}
    </a>
  </header>
}

<div
  [style]="'--bg-color:' + (booking.color || '#3158e3')"
  class="wrapper text-color-{{ getContrastYIQ(booking.color || '#3158e3') }}"
  *ngIf="booking"
  [class.last-step]="selectedTime || booking.is_offline"
  [class.submitted]="submitted"
  [class.preview]="isPreview"
>
  <div class="container">
    @if (!booking.is_offline) {
      <header class="text-center" [class.d-none]="(selectedTime && (isTouch$ | async) === false) || submitted">
        @if (isPreview) {
          <div class="troubleshoot">
            <cf-switch labelPosition="before" [(ngModel)]="troubleshoot">
              <span class="tw:text-lg tw:font-medium">
                {{ "booking.troubleshoot_label" | translate }}
              </span>
            </cf-switch>
          </div>
        }
        <div class="heading" *ngIf="booking.with_picture">
          <cf-avatar
            *ngIf="booking.picture as picture"
            [src]="$any(picture.image)"
            [rounded]="$any(picture.rounded)"
            size="xl"
            [userName]="booking.organizer_name"
          ></cf-avatar>
          <cf-avatar *ngIf="!booking.picture" [userName]="booking.organizer_name" size="xl"></cf-avatar>
        </div>

        <h2 class="mt-3 mb-2">{{ booking.public_title }}</h2>
        <cf-text weight="500" size="md" color="white" class="text-pre-line">
          <cf-text-truncate
            color="white"
            [count]="50"
            [text]="booking.description ?? ''"
            entityName=" {{ 'description' | translate }}"
          ></cf-text-truncate>
        </cf-text>

        @if (booking.paid_events) {
          <cf-text weight="500" size="sm" color="white">
            <div class="d-flex align-items-center justify-content-center mt-2 text-center">
              <svg cfIcon="credit_card" class="tw:mr-1"></svg>
              @if (booking.pricing_model === EPricingModel.HOURLY) {
                {{
                  "payments.pricing_model.hourly"
                    | translate: { price: booking.price, currency_code: booking.currency_code }
                }}
              } @else if (booking.pricing_model === EPricingModel.PER_ATTENDEE) {
                {{
                  "payments.pricing_model.per_attendee"
                    | translate: { price: booking.price, currency_code: booking.currency_code }
                }}
              } @else {
                {{
                  "payments.pricing_model.fixed"
                    | translate: { price: booking.price, currency_code: booking.currency_code }
                }}
              }
            </div>
          </cf-text>
        }

        <cf-booking-information
          *ngIf="selectedSlot && (isTouch$ | async)"
          [day]="selectedSlot.date"
          [time]="selectedTime"
          [timezone]="timezone"
          [duration]="selectedDuration"
          [location]="booking.location"
          class="text-force-white mt-4"
        ></cf-booking-information>

        <cf-booking-steps
          (emitStep)="stepChange($event)"
          class="d-block mt-4"
          *ngIf="isTouch$ | async"
          [stepIndex]="stepIndex"
        ></cf-booking-steps>
      </header>

      @if (isPreview && submitted) {
        <header class="mb-5 pb-0 text-center">
          <a cf-link (click)="refresh()" class="mb-3">
            <svg cfIcon="update" class="tw:mx-0"></svg>
            {{ "refresh" | translate }}
          </a>
          <cf-text size="sm" class="mb-3" *ngIf="url !== '/dashboard/meeting/create'">
            {{ "booking.preview_thank_you" | translate | translateCut: 0 }}
            <a
              cf-link
              weight="500"
              size="sm"
              routerLink="/{{ params.username }}/{{ params.path }}/{{ selectedDuration }}"
              target="_blank"
              [underlined]="true"
            >
              {{ "booking.preview_thank_you" | translate | translateCut: 1 }}
            </a>
          </cf-text>
        </header>
      }
    }

    <section
      cf-loader-container
      *ngIf="timeslots"
      [class.personal-information]="selectedTime || booking.is_offline"
      [class.m-auto]="booking.is_offline"
      [class.is-offline]="booking.is_offline"
    >
      <ng-container *ngIf="!submitted && !booking.is_offline">
        <ng-container *ngIf="!selectedTime">
          <div class="step" [class.m-auto]="!selectedSlot" *ngIf="!selectedSlot || (isTouch$ | async) === false">
            <div class="step-header">
              <h5 class="d-flex flex-column">
                <cf-text color="grey-70" weight="500" *ngIf="isTouch$ | async"> {{ "step" | translate }} 1: </cf-text>
                {{ "booking.select_date" | translate }}
              </h5>
            </div>
            <cf-booking-calendar
              [day]="selectedSlot ? selectedSlot.date : undefined"
              [timeslots]="timeslots"
              (emitSlot)="getEvents($event)"
              (emitDateRange)="applyDate($event)"
              [booking]="booking"
              [selected]="selectedDate"
              [isTouch]="isTouch$ | async"
              [hasFreeSlots]="hasFreeSlots"
            ></cf-booking-calendar>
          </div>

          <ng-container *ngIf="selectedSlot">
            <div class="step ms-xl-5 me-xl-5 hide-scrollbar">
              <div class="step-header">
                <h5 class="d-flex flex-column">
                  <cf-text color="grey-70" weight="500" *ngIf="isTouch$ | async">{{ "step" | translate }} 2: </cf-text>
                  <span>
                    {{ "booking.select_duration_time" | translate }}
                    <svg
                      cfIcon="question_circle_bold"
                      size="sm"
                      [ngbPopover]="
                        booking.durations.length < 2
                          ? ('booking.preview_duration_short_hint' | translate)
                          : ('booking.preview_duration_hint' | translate)
                      "
                      popoverClass="hint"
                      triggers="hover"
                      container="body"
                    ></svg>
                  </span>
                </h5>
              </div>
              <cf-booking-picker
                [hasFreeSlots]="hasFreeSlots"
                [isTouch]="isTouch$ | async"
                [troubleshoot]="troubleshoot"
                [hourFormat]="booking.time_format"
                [timezone]="timezone"
                [disableTimezone]="disableTimezone"
                [isPreview]="isPreview"
                [isAuthorized]="authService.isAuthorized$ | async"
                [duration]="selectedDuration"
                [timeslot]="selectedSlot"
                [timeslots]="timeslots"
                [timezoneSetting]="booking.timezone_setting || ETimezoneSettings.AUTO_DETECT"
                [durations]="booking.durations"
                [events]="events"
                (emitTimezone)="getTimeslots($event)"
                (emitConfirm)="confirm($event)"
                (emitFocus)="focusTime($event)"
                (emitDuration)="durationChanged($event)"
                (emitNextSlot)="setFreeSlot()"
              ></cf-booking-picker>
            </div>

            <div class="step availability-step" *ngIf="(isTouch$ | async) === false">
              <div class="step-header">
                <h5>{{ "booking.see_availability" | translate }}</h5>
              </div>

              <cf-booking-availability
                [events]="events"
                [date]="selectedSlot.date"
                [isAuthorized]="(authService.isAuthorized$ | async) && !preview"
                [isPreview]="isPreview || preview"
                [timeFormat]="accountService.account.time_format"
              ></cf-booking-availability>
            </div>
          </ng-container>
        </ng-container>
        <ng-container *ngIf="selectedTime">
          <div class="step personal-information">
            <div class="step-header d-xl-none">
              <h5 class="d-flex flex-column">
                <cf-text color="grey-70" weight="500" *ngIf="isTouch$ | async"> {{ "step" | translate }} 3: </cf-text>
                @if (accountService.account.email) {
                  {{ "booking.your_information" | translate }}
                } @else {
                  {{ "booking.fill_information" | translate }}
                }
              </h5>
            </div>
            <cf-booking-personal
              [form]="form"
              [booking]="booking"
              [timezone]="timezone"
              [time]="selectedTime"
              [day]="selectedSlot.date"
              [duration]="selectedDuration"
              [isPreview]="isPreview || preview"
              (emitBack)="back()"
              (emitSubmit)="submit($event, booking)"
              [previewMode]="previewMode"
            ></cf-booking-personal>
          </div>
        </ng-container>
      </ng-container>

      @if (booking.is_offline) {
        <div class="d-flex flex-column w-100 text-center">
          <h2 class="mb-4">
            {{ booking.public_title }}
            @if (booking.offline_message) {
              <br />
              {{ booking.offline_message }}
            }
          </h2>
          <cf-text size="lg" color="grey-70" weight="500">
            {{ "booking.offline_message" | translate | translateCut: 0 }}
            <a cf-link size="lg" (click)="loginRedirect(environment.appUrl + '/auth')">
              {{ "booking.offline_message" | translate | translateCut: 1 }}
            </a>
            {{ "booking.offline_message" | translate | translateCut: 2 }}
          </cf-text>
        </div>
      }

      <ng-container *ngIf="submitted">
        <div class="step personal-information">
          <cf-booking-confirmation
            [eventAddedToCalendar]="eventAddedToCalendar"
            [heading]="booking.tentative_bookings ? ('booking.requested' | translate) : ('confirmed' | translate)"
            [description]="
              booking.tentative_bookings
                ? ('booking.requested_text' | translate)
                : ('booking.confirmation_text' | translate: { name: booking.organizer_name })
            "
            [booking]="booking"
            [selectedDuration]="selectedDuration"
            [selectedSlot]="selectedSlot"
            [selectedTime]="selectedTime"
            [timezone]="timezone"
            [account]="accountService.account"
            [participantsAmount]="participantsAmount"
            [isPreview]="isPreview || preview"
            (scheduleAnother)="scheduleAnother()"
          ></cf-booking-confirmation>
        </div>
      </ng-container>
    </section>

    <cf-booking-footer
      *ngIf="!booking.is_offline"
      [showLogo]="booking.show_cal_frenzy_logo ?? true"
      [isTouch]="isTouch"
      [isPreview]="isPreview || preview"
      [cancellationPolicy]="
        booking.use_cancellation_policy && !booking.is_offline ? (booking.cancellation_policy ?? '') : ''
      "
    ></cf-booking-footer>
  </div>
</div>
